import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, Camera, MapPin, Clock, Users, ArrowRight, Building } from 'lucide-react';
import { useFooterGlow } from '../context/FooterGlowContext';
import './Headshots.css';

const Headshots = () => {
  const { triggerGlow } = useFooterGlow();

  return (
    <div className="headshots-page">
      {/* Hero Section */}
      <section className="hero-section" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/x-sLGYaQ_stMM-unsplash.jpg)`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}>
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1 
              className="hero-title"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Business & Personal Headshots
            </motion.h1>
            <motion.p 
              className="hero-subtitle"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Professional headshots for your business, career, and personal branding
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Service Details */}
      <section className="service-details section">
        <div className="container">
          <div className="details-grid">
            <motion.div 
              className="details-content"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2>Professional Headshots That Stand Out</h2>
              <p>
                In today's digital world, your headshot is often the first impression people have of you. 
                At Vellichor Visions, we create professional headshots for both business and personal use 
                that convey confidence, professionalism, and authenticity.
              </p>
              <p>
                Whether you need business headshots for your LinkedIn profile, company website, or marketing 
                materials, or personal headshots for social media, dating profiles, or personal branding, 
                we'll work with you to create images that represent your unique personality and style.
              </p>
              
              <div className="service-features">
                <div className="feature-item">
                  <Building size={24} />
                  <div>
                    <h4>Business & Personal</h4>
                    <p>Professional headshots for corporate use, LinkedIn, social media, and personal branding</p>
                  </div>
                </div>
                <div className="feature-item">
                  <MapPin size={24} />
                  <div>
                    <h4>On-Location Service</h4>
                    <p>We'll come to your office or preferred location for maximum convenience</p>
                  </div>
                </div>
                <div className="feature-item">
                  <Camera size={24} />
                  <div>
                    <h4>Multiple Options</h4>
                    <p>We capture various poses and expressions to give you plenty of choices</p>
                  </div>
                </div>
                <div className="feature-item">
                  <Clock size={24} />
                  <div>
                    <h4>Quick Sessions</h4>
                    <p>Efficient 30-45 minute sessions that fit into your busy schedule</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="details-image"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img 
                src={`${process.env.PUBLIC_URL}/M7404838.jpg`}
                alt="Professional headshot example"
                className="headshot-image"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="included-section section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>What's Included</h2>
            <p>Everything you need for your business or personal headshot session</p>
          </motion.div>

          <div className="included-grid">
            <motion.div 
              className="included-item"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="item-icon">
                <Users size={32} />
              </div>
              <h3>Pre-Session Consultation</h3>
              <p>We'll discuss your goals, whether for business or personal use, and how you want to be portrayed.</p>
            </motion.div>

            <motion.div 
              className="included-item"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="item-icon">
                <Camera size={32} />
              </div>
              <h3>Professional Photography</h3>
              <p>30-45 minute session with professional lighting and equipment setup.</p>
            </motion.div>

            <motion.div 
              className="included-item"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <div className="item-icon">
                <Star size={32} />
              </div>
              <h3>Professional Editing</h3>
              <p>Retouching and color correction to ensure you look your absolute best.</p>
            </motion.div>

            <motion.div 
              className="included-item"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <div className="item-icon">
                <Clock size={32} />
              </div>
              <h3>Quick Delivery</h3>
              <p>Your edited headshots delivered within 1 week of your session.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Packages */}
      <section className="team-section section">
        <div className="container">
          <motion.div 
            className="team-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Team & Group Packages</h2>
            <p>
              Need headshots for your entire team or group? We offer special packages for companies 
              looking to update their team photos, executive portraits, or company website imagery. 
              We also offer packages for personal groups or families looking for coordinated headshots.
            </p>
            <div className="team-features">
              <div className="team-feature">
                <Building size={24} />
                <span>Bulk pricing for teams of 5+ people</span>
              </div>
              <div className="team-feature">
                <MapPin size={24} />
                <span>On-site sessions at your office</span>
              </div>
              <div className="team-feature">
                <Clock size={24} />
                <span>Flexible scheduling for your team</span>
              </div>
            </div>
            <div className="team-cta">
              <Link to="/booking" className="btn btn-primary">
                Get Team Quote
                <ArrowRight size={20} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing */}
      <section className="pricing-section section">
        <div className="container">
          <motion.div 
            className="pricing-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Pricing</h2>
            <p>
              Headshot pricing varies based on your needs - whether for business or personal use, 
              and whether you're booking individually or as part of a team. Contact us for a personalized quote.
            </p>
            <div className="pricing-cta">
              <Link to="/booking" className="btn btn-primary" onClick={triggerGlow}>
                Get Your Quote
                <ArrowRight size={20} />
              </Link>
              <a href="mailto:vellichorvisions@gmail.com" className="btn btn-outline">
                Email for Pricing
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section section">
        <div className="container">
          <motion.div 
            className="cta-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Ready for Your Headshot?</h2>
            <p>Let's create a professional headshot that makes the right impression for your business, career, or personal brand.</p>
            <Link to="/booking" className="btn btn-primary" onClick={triggerGlow}>
              Book Your Session
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Headshots; 